// İnternetsiz növbə (KT-108, mobil skaner): sayım oxunuşları və qəbul sənədi brauzerdə (IndexedDB) saxlanır, internet gələndə
// göndərilir. Hər element öz UUID-i ilə gedir — server təkrarı tanıyır (idempotent), ikinci dəfə yazmır.
// IndexedDB yoxdursa (köhnə brauzer, gizli rejim) — yaddaşda (səhifə bağlananda itir; ekranda xəbərdarlıq).

export interface QueueItem {
  id: string;
  /** Göndəriləcək ünvan və gövdə (POST) */
  path: string;
  body: unknown;
  createdAt: string;
  /** Server rədd edibsə (4xx) — təkrar göndərilmir, istifadəçiyə göstərilir */
  error: string | null;
  /** İnsan dilində qısa ad (məs. «Qəbul: 3 mal») */
  label: string;
  /** Ekran üçün əlavə (məs. qəbul sətirləri adla — rədd olunanı geri yükləmək üçün) */
  meta?: unknown;
}

export interface QueueBackend {
  all(): Promise<QueueItem[]>;
  put(item: QueueItem): Promise<void>;
  del(id: string): Promise<void>;
  durable: boolean;
}

export function memoryBackend(): QueueBackend {
  const m = new Map<string, QueueItem>();
  return {
    durable: false,
    all: async () => [...m.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    put: async (i) => void m.set(i.id, i),
    del: async (id) => void m.delete(id),
  };
}

const DB = 'kassa-offline';
const STORE = 'queue';

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => {
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error ?? new Error('IndexedDB xətası'));
  });
}

export async function indexedDbBackend(): Promise<QueueBackend | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const open = indexedDB.open(DB, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE, { keyPath: 'id' });
    // Başqa vərəq köhnə versiyanı tutursa (blocked) və ya açılış heç bitmirsə — yaddaşa keçirik, ekran asılı qalmasın
    const db = await Promise.race([
      req(open),
      new Promise<never>((_, rej) => {
        open.onblocked = () => rej(new Error('IndexedDB bloklanıb'));
        setTimeout(() => rej(new Error('IndexedDB açılmadı (vaxt bitdi)')), 5000);
      }),
    ]);
    const tx = (mode: IDBTransactionMode) => db.transaction(STORE, mode).objectStore(STORE);
    return {
      durable: true,
      all: async () => ((await req(tx('readonly').getAll())) as QueueItem[]).sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
      put: async (i) => void (await req(tx('readwrite').put(i))),
      del: async (id) => void (await req(tx('readwrite').delete(id))),
    };
  } catch (e) {
    console.error('kassa.offline: IndexedDB açılmadı', e instanceof Error ? e.message : e);
    return null;
  }
}

/**
 * Göndərmə nəticəsi: ok — silinir; rejected — server rədd etdi (saxlanır, təkrar göndərilmir, istifadəçi silir və ya geri yükləyir);
 * network — dayanırıq, sonra yenidən; auth — sessiya bitib (yenidən giriş), dayanırıq; skip — bu sessiyanın deyil (başqa istifadəçi), toxunulmur.
 */
export type SendResult = { kind: 'ok' } | { kind: 'rejected'; message: string } | { kind: 'network' } | { kind: 'auth' } | { kind: 'skip' };

export interface FlushResult {
  sent: number;
  pending: number;
  rejected: number;
  /** Cihaz yaddaşına yazıla bilməyən addımlar (növbə yenidən cəhddə düzəlir — server təkrarı tanıyır) */
  storeErrors: number;
  stopped: 'network' | 'auth' | null;
}

/** Növbəni sıra ilə göndərir. Şəbəkə/giriş xətasında dayanır (sıra pozulmasın). */
export async function flushQueue(backend: QueueBackend, send: (i: QueueItem) => Promise<SendResult>): Promise<FlushResult> {
  let sent = 0;
  let storeErrors = 0;
  let stopped: FlushResult['stopped'] = null;
  for (const item of await backend.all()) {
    if (item.error) continue;
    const r = await send(item);
    if (r.kind === 'network' || r.kind === 'auth') {
      stopped = r.kind;
      break;
    }
    if (r.kind === 'skip') continue;
    try {
      if (r.kind === 'ok') {
        await backend.del(item.id);
        sent += 1;
      } else {
        await backend.put({ ...item, error: r.message });
      }
    } catch (e) {
      storeErrors += 1;
      console.error('kassa.offline: növbə yaddaşı yazılmadı', e instanceof Error ? e.message : e);
    }
  }
  const rest = await backend.all();
  return { sent, pending: rest.filter((i) => !i.error).length, rejected: rest.filter((i) => i.error).length, storeErrors, stopped };
}
